// How each thing in the notebook is drawn. Everything is strokes: no sprites, no images.
import { inkLine, inkPath, inkEllipse, hatch, inkBlot, penText, groundShade, pencil, pencilBlob, scribble, C, INK, INK_SOFT, PENCIL, PENCIL_SOFT, RED, GREEN, GOLD, MARGIN, W } from './ink.ts';
import type { EntityState, ZoneProp } from '../../../packages/protocol/src/index.ts';
import { ITEMS } from '../../../packages/game-data/src/index.ts';
import { itemIcon } from './ui.ts';
import { bake, blit, spriteScale } from './sprite.ts';

const T = (t: number) => t; // readability in the animation lines below

// ---------- world props ----------
// Every prop is drawn with the same three weights: a heavy contour, light interior detail,
// and pencil hatching on the side away from the light (which always comes from the upper left).

const rnd = (seed: number, i: number) => {
  const h = Math.imul((seed + i * 2654435761) ^ 0x9e3779b9, 2246822519) >>> 0;
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
};

/** the drifting crests, drawn live over the baked river */
export function waterCrests(ctx: CanvasRenderingContext2D, p: ZoneProp, t: number) {
  const { x, y, w, h, seed } = p;
  const rows = Math.ceil(h / 34);
  for (let i = 0; i < rows; i++) {
    const wy = y + i * 34 + 10 + Math.sin(t * 0.55 + i * 0.8) * 3;
    const off = ((i % 3) - 1) * 16;
    for (let k = 0; k < 3; k++) {
      const wx = x + 18 + k * (w / 3) + off;
      inkPath(ctx, [[wx - 7, wy], [wx - 3, wy - 3.2], [wx + 1, wy], [wx + 5, wy - 3], [wx + 9, wy]], { color: INK_SOFT, width: W.detail, seed: seed + i * 5 + k, amp: 0.8, alpha: 0.75 });
    }
  }
}

export function drawProp(ctx: CanvasRenderingContext2D, p: ZoneProp, t: number, skipStatic = false) {
  const { x, y, w, h, seed } = p;
  switch (p.kind) {
    case 'tree': drawTree(ctx, p); break;
    case 'rock': {
      // a boulder, not a cut-out: a rounded silhouette, two soft creases, shading down one side
      groundShade(ctx, x + w * 0.55, y + h - 1, w * 0.66, seed, 0.15);
      const cxr = x + w * 0.5, cyr = y + h * 0.52, rxr = w * 0.5, ryr = h * 0.48;
      const o: [number, number][] = [];
      const pts = 16;
      for (let i = 0; i < pts; i++) {
        const a = (i / pts) * Math.PI * 2 - 1.6;
        const r = 0.88 + rnd(seed + 3, i) * 0.2;
        const flat = Math.sin(a) > 0.55 ? 0.82 : 1;     // sits flat on the ground
        o.push([cxr + Math.cos(a) * rxr * r, cyr + Math.sin(a) * ryr * r * flat]);
      }
      pencil(ctx, o, { color: PENCIL, width: W.contour, seed, amp: 0.6, double: true, fill: C.stone, fillAlpha: 0.5, smooth: true });
      // shading down the right and under: two densities, so the stone has a lit side
      ctx.save();
      ctx.beginPath();
      for (let i2 = 0; i2 < o.length; i2++) {
        const a2 = o[i2]!, b2 = o[(i2 + 1) % o.length]!, prev = o[(i2 + o.length - 1) % o.length]!;
        const m0: [number, number] = [(prev[0] + a2[0]) / 2, (prev[1] + a2[1]) / 2];
        const m1: [number, number] = [(a2[0] + b2[0]) / 2, (a2[1] + b2[1]) / 2];
        if (i2 === 0) ctx.moveTo(m0[0], m0[1]);
        ctx.quadraticCurveTo(a2[0], a2[1], m1[0], m1[1]);
      }
      ctx.closePath(); ctx.clip();
      hatch(ctx, x + w * 0.44, y, w * 0.6, h, { color: C.stoneDark, gap: 3, angle: -1.15, alpha: 0.42, seed: seed + 3 });
      hatch(ctx, x, y + h * 0.58, w * 0.5, h * 0.5, { color: C.stoneDark, gap: 5.5, angle: -1.15, alpha: 0.2, seed: seed + 4 });
      hatch(ctx, x + w * 0.1, y + h * 0.05, w * 0.35, h * 0.3, { color: '#ffffff', gap: 4.5, angle: -0.5, alpha: 0.35, seed: seed + 5 });
      ctx.restore();
      // creases: short, broken, following the form rather than cutting across it
      inkPath(ctx, [[x + w * 0.3, y + h * 0.2], [x + w * 0.42, y + h * 0.4], [x + w * 0.38, y + h * 0.62]],
        { color: PENCIL_SOFT, width: W.detail, seed: seed + 8, amp: 0.7, alpha: 0.6 });
      inkPath(ctx, [[x + w * 0.58, y + h * 0.26], [x + w * 0.68, y + h * 0.46]],
        { color: PENCIL_SOFT, width: W.shade, seed: seed + 9, amp: 0.5, alpha: 0.45 });
      // moss where it meets the ground, and a chip of stone beside it
      for (let i = 0; i < 6; i++) {
        const mx = x + w * (0.1 + i * 0.14), my = y + h * (0.9 + rnd(seed + 11, i) * 0.08);
        inkPath(ctx, [[mx, my], [mx + 3, my - 4], [mx + 6, my]], { color: C.grassDark, width: W.shade, seed: seed + 12 + i, amp: 0.3, alpha: 0.55 });
      }
      pencil(ctx, [[x + w * 0.02, y + h * 0.96], [x - w * 0.16, y + h * 0.82], [x - w * 0.08, y + h * 0.68], [x + w * 0.08, y + h * 0.82]],
        { color: PENCIL, width: W.edge, seed: seed + 5, amp: 0.7, fill: C.stone, fillAlpha: 0.5, shade: C.stoneDark, shadeAlpha: 0.26, gap: 3, angle: -1.15, smooth: true });
      break;
    }
    case 'hut': {
      drawCottage(ctx, p);
      break;
    }
    case 'fence': {
      // Posts are a fixed height in the world, whatever the length of the run: a fence comes up to
      // about the chest of the person walking past it.
      const horiz = w > h;
      const postH = 54, rail1 = postH * 0.32, rail2 = postH * 0.72;
      if (horiz) {
        const n = Math.max(2, Math.round(w / 78));
        const yBase = y + h;
        // the shadow the whole run throws
        ctx.save(); ctx.globalAlpha = 0.1;
        hatch(ctx, x, yBase - 2, w, 12, { color: PENCIL, gap: 4, angle: -0.5, alpha: 0.5, seed: seed + 99 });
        ctx.restore();
        // rails behind the posts
        for (const r of [rail1, rail2]) {
          pencil(ctx, [[x, yBase - r - 5], [x + w, yBase - r - 5], [x + w, yBase - r + 1], [x, yBase - r + 1]],
            { color: PENCIL, width: W.edge, seed: seed + r, amp: 1.1, fill: C.trunk, fillAlpha: 0.6, shade: C.trunkDark, shadeAlpha: 0.18, gap: 5, angle: 0 });
        }
        for (let i = 0; i <= n; i++) {
          const px = x + (w * i) / n, lean = (rnd(seed, i) - 0.5) * 4;
          const pw = 7;
          pencil(ctx, [[px - pw + lean, yBase - postH], [px + pw + lean, yBase - postH], [px + pw * 0.8, yBase], [px - pw * 0.8, yBase]],
            { color: PENCIL, width: W.edge, seed: seed + i, amp: 0.6, fill: C.trunk, fillAlpha: 0.72, shade: C.trunkDark, shadeAlpha: 0.26, gap: 3.2, angle: -1.44 });
          // the sawn top and the nails that hold the rails on
          inkLine(ctx, px - pw + lean, yBase - postH, px + pw + lean, yBase - postH, { color: C.trunkDark, width: W.detail, amp: 0.4, alpha: 0.8, seed: seed + i + 20 });
          for (const r of [rail1, rail2]) { ctx.fillStyle = PENCIL; ctx.beginPath(); ctx.arc(px + lean * 0.8, yBase - r - 2, 1.4, 0, 7); ctx.fill(); }
        }
      } else {
        // A run going away from you: the posts march up the page and the rails run between them.
        // They have to be spaced further apart than they are tall, or the run reads as a ladder.
        const n = Math.max(2, Math.round(h / 104));
        const cxf = x + w / 2, ph = 46;
        const post = (i: number) => ({ px: cxf + (rnd(seed, i) - 0.5) * 5, py: y + (h * i) / n });
        // Seen almost edge on, the rails are foreshortened to a line: draw them as one, between
        // each pair of posts, or the run turns into a solid pole down the page.
        for (let i = 0; i < n; i++) {
          const a0 = post(i), b0 = post(i + 1);
          for (const r of [ph * 0.34, ph * 0.74]) {
            inkLine(ctx, a0.px, a0.py - r, b0.px, b0.py - r, { color: C.trunkDark, width: W.edge, amp: 0.7, alpha: 0.85, seed: seed + i * 5 + r });
          }
        }
        for (let i = 0; i <= n; i++) {
          const { px, py } = post(i);
          ctx.save(); ctx.globalAlpha = 0.12;
          hatch(ctx, px - 14, py - 3, 30, 10, { color: PENCIL, gap: 4, angle: -0.5, alpha: 0.5, seed: seed + 70 + i });
          ctx.restore();
          pencil(ctx, [[px - 6, py - ph], [px + 6, py - ph], [px + 5, py], [px - 5, py]],
            { color: PENCIL, width: W.edge, seed: seed + i, amp: 0.6, fill: C.trunk, fillAlpha: 0.75, shade: C.trunkDark, shadeAlpha: 0.26, gap: 3.2, angle: -1.44 });
          inkLine(ctx, px - 6, py - ph, px + 6, py - ph, { color: C.trunkDark, width: W.detail, amp: 0.4, alpha: 0.8, seed: seed + i + 20 });
        }
      }
      break;
    }
    case 'water': {
      if (skipStatic) { waterCrests(ctx, p, t); break; }
      // The bank is never straight. Build the channel as one wavy shape and colour inside it.
      const steps = Math.max(6, Math.round(h / 60));
      const edge = (side: -1 | 1, i: number) => {
        const f = i / steps;
        const wob = Math.sin(f * 6.2 + seed * 0.7 + (side > 0 ? 1.9 : 0)) * w * 0.07
          + (rnd(seed + (side > 0 ? 70 : 40), i) - 0.5) * w * 0.05;
        return side < 0 ? x + w * 0.04 + wob : x + w * 0.96 + wob;
      };
      const chan: [number, number][] = [];
      for (let i = 0; i <= steps; i++) chan.push([edge(-1, i), y + (h * i) / steps]);
      for (let i = steps; i >= 0; i--) chan.push([edge(1, i), y + (h * i) / steps]);
      ctx.save();
      ctx.beginPath();
      chan.forEach(([px, py], i) => (i ? ctx.lineTo(px, py) : ctx.moveTo(px, py)));
      ctx.closePath();
      ctx.globalAlpha = 0.66; ctx.fillStyle = C.water; ctx.fill();
      ctx.globalAlpha = 1;
      ctx.clip();
      // deeper down the middle, shallow and paler at the edges
      hatch(ctx, x + w * 0.2, y, w * 0.6, h, { color: C.waterDark, gap: 6, angle: 1.35, alpha: 0.2, seed: seed + 21 });
      hatch(ctx, x, y, w * 0.16, h, { color: C.waterDark, gap: 3.6, angle: 1.35, alpha: 0.26, seed: seed + 22 });
      hatch(ctx, x + w * 0.84, y, w * 0.16, h, { color: C.waterDark, gap: 3.6, angle: 1.35, alpha: 0.26, seed: seed + 23 });
      // lily pads, in clusters, some with a flower
      const pads = Math.round(h / 130);
      for (let i = 0; i < pads; i++) {
        const cxp = x + w * (0.22 + rnd(seed + 51, i) * 0.56);
        const cyp = y + h * ((i + 0.5) / pads) + (rnd(seed + 52, i) - 0.5) * h * 0.1;
        for (let k = 0; k < 3; k++) {
          const px = cxp + (rnd(seed + 53, i * 3 + k) - 0.5) * w * 0.3;
          const py = cyp + (rnd(seed + 54, i * 3 + k) - 0.5) * h * 0.05;
          const pr = w * (0.07 + rnd(seed + 55, i * 3 + k) * 0.05);
          const a0 = rnd(seed + 56, i * 3 + k) * 6.28;
          const pts: [number, number][] = [];
          for (let q = 0; q <= 14; q++) {
            const a = a0 + 0.55 + (q / 14) * (Math.PI * 2 - 1.1);
            pts.push([px + Math.cos(a) * pr, py + Math.sin(a) * pr * 0.62]);
          }
          pts.push([px, py]);
          pencil(ctx, pts, { color: C.grassDark, width: W.detail, seed: seed + i * 7 + k, amp: 0.4, smooth: true, fill: C.grass, fillAlpha: 0.85 });
          if (rnd(seed + 57, i * 3 + k) > 0.72) {
            const fx = px + pr * 0.2, fy = py - pr * 0.3;
            for (let q = 0; q < 5; q++) {
              const a = (q / 5) * 6.28;
              ctx.fillStyle = '#e6a8c8'; ctx.globalAlpha = 0.9;
              ctx.beginPath(); ctx.ellipse(fx + Math.cos(a) * 2.6, fy + Math.sin(a) * 2, 2, 1.4, a, 0, 7); ctx.fill();
            }
            ctx.globalAlpha = 1;
            ctx.fillStyle = GOLD; ctx.beginPath(); ctx.arc(fx, fy, 1.4, 0, 7); ctx.fill();
          }
        }
      }
      ctx.restore();
      // the banks themselves, drawn over the fill
      const bank = (side: -1 | 1) => {
        const pts: [number, number][] = [];
        for (let i = 0; i <= steps; i++) pts.push([edge(side, i), y + (h * i) / steps]);
        inkPath(ctx, pts, { color: INK_SOFT, width: W.contour, seed: seed + (side > 0 ? 8 : 7), amp: 1.4 });
      };
      bank(-1); bank(1);
      // stones and reeds along both banks
      for (let i = 0; i < Math.round(h / 90); i++) {
        for (const side of [-1, 1] as const) {
          const f = (i + rnd(seed + 61, i) * 0.8) / Math.round(h / 90);
          const by = y + h * Math.min(0.98, f);
          const bx = edge(side, Math.min(steps, Math.round(f * steps))) + side * w * 0.03;
          if (rnd(seed + 62, i * 2 + (side > 0 ? 1 : 0)) > 0.5) {
            const sr = w * 0.05;
            pencilBlob(ctx, bx, by, sr, sr * 0.7, { color: PENCIL, width: W.detail, seed: seed + i * 11 + side, amp: 0.5,
              fill: C.stone, fillAlpha: 0.5, shade: C.stoneDark, shadeAlpha: 0.25, gap: 3, angle: -1.2 });
          } else {
            for (let k = 0; k < 4; k++) {
              inkPath(ctx, [[bx + k * 2.2, by], [bx + side * (k * 2 + 3), by - 12 - rnd(seed + k, i) * 8]],
                { color: C.grassDark, width: W.shade, seed: seed + i * 7 + k, amp: 0.45, alpha: 0.7 });
            }
          }
        }
      }
      break;
    }
    case 'bridge': {
      // a plank bridge seen from three quarters: deck, kerbs, rails on posts, piles in the water
      const across = w > h;                       // the way you walk over it
      const deckT = y + h * 0.1, deckB = y + h * 0.9;
      // the shadow it throws on the water
      ctx.save(); ctx.globalAlpha = 0.16; ctx.fillStyle = C.waterDark;
      ctx.fillRect(x + 6, deckT + 8, w, deckB - deckT); ctx.restore();
      // piles going down into the water
      const piles = Math.max(2, Math.round(w / 110));
      for (let i = 0; i < piles; i++) {
        const px = x + w * ((i + 0.5) / piles);
        for (const py of [deckT + 4, deckB - 2]) {
          pencil(ctx, [[px - 5, py], [px + 5, py], [px + 4, py + 16], [px - 4, py + 16]],
            { color: PENCIL, width: W.detail, seed: seed + i * 5 + py, amp: 0.4, fill: C.trunkDark, fillAlpha: 0.7 });
        }
      }
      // the deck
      pencil(ctx, [[x, deckT], [x + w, deckT], [x + w, deckB], [x, deckB]],
        { color: PENCIL, width: W.contour, seed, amp: 0.8, fill: C.timber, fillAlpha: 0.62, shade: C.trunkDark, shadeAlpha: 0.14, gap: 8, angle: 0 });
      const planks = Math.max(6, Math.round((across ? w : h) / 26));
      for (let i = 1; i < planks; i++) {
        const f = i / planks;
        if (across) {
          const px = x + w * f;
          inkLine(ctx, px, deckT + 2, px, deckB - 2, { color: C.trunkDark, width: W.edge, amp: 0.7, alpha: 0.8, seed: seed + i });
          for (const ny of [deckT + 6, deckB - 6]) { ctx.fillStyle = PENCIL; ctx.beginPath(); ctx.arc(px - 3, ny, 1.2, 0, 7); ctx.fill(); }
        } else {
          const py = y + h * f;
          inkLine(ctx, x + 2, py, x + w - 2, py, { color: C.trunkDark, width: W.detail, amp: 0.7, alpha: 0.55, seed: seed + i });
        }
      }
      // kerbs along both sides, then the rails standing on them
      for (const ky of [deckT, deckB]) {
        inkLine(ctx, x, ky, x + w, ky, { color: PENCIL, width: W.edge, amp: 0.9, seed: seed + ky });
      }
      const posts = Math.max(3, Math.round(w / 90));
      for (const side of [0, 1]) {
        const ky = side ? deckB : deckT;
        const lift = side ? 26 : 20;                    // the far rail is drawn a little shorter
        const railY = ky - lift;
        for (let i = 0; i <= posts; i++) {
          const px = x + (w * i) / posts;
          pencil(ctx, [[px - 3, ky + 1], [px + 3, ky + 1], [px + 2.4, railY], [px - 2.4, railY]],
            { color: PENCIL, width: W.detail, seed: seed + 30 + i + side * 7, amp: 0.4, fill: C.trunk, fillAlpha: 0.7 });
        }
        for (const ry of [railY + 2, railY + lift * 0.5]) {
          inkLine(ctx, x, ry, x + w, ry, { color: PENCIL, width: W.edge, amp: 1, alpha: 0.95, seed: seed + ry + side });
        }
      }
      break;
    }
    case 'sign': {
      groundShade(ctx, x + w / 2, y + h, w * 0.5, seed, 0.12);
      inkPath(ctx, [[x + w / 2 - 2.4, y + h], [x + w / 2 + 2.4, y + h], [x + w / 2 + 2, y + h * 0.45], [x + w / 2 - 2, y + h * 0.45]], { color: PENCIL, width: W.edge, seed, amp: 0.8, close: true, fill: C.trunk });
      const bw = w + 26, bh = h * 0.58;
      inkPath(ctx, [[x + w / 2 - bw / 2, y - 4], [x + w / 2 + bw / 2, y - 6], [x + w / 2 + bw / 2, y + bh - 6], [x + w / 2 - bw / 2, y + bh - 4]],
        { color: INK, width: W.contour, seed: seed + 1, amp: 0.7, close: true, double: true, fill: 'rgba(226,205,169,0.95)' });
      for (const cxn of [x + w / 2 - bw / 2 + 5, x + w / 2 + bw / 2 - 5]) { ctx.fillStyle = INK; ctx.beginPath(); ctx.arc(cxn, y + 2, 1.2, 0, 7); ctx.fill(); }
      if (p.text) penText(ctx, p.text, x + w / 2, y + bh * 0.62 - 5, { size: 13, align: 'center', color: INK, seed: seed + 2 });
      break;
    }
    case 'grass': {
      // a soft green ground first, so the field reads as a meadow rather than as blades on paper
      for (let i = 0; i < 16; i++) {
        const gx0 = x + rnd(seed + 71, i) * p.w, gy0 = y + rnd(seed + 72, i) * p.h;
        const rx0 = p.w * (0.07 + rnd(seed + 73, i) * 0.07), ry0 = p.h * (0.1 + rnd(seed + 74, i) * 0.1);
        scribble(ctx, gx0, gy0, rx0, ry0, { color: C.grass, alpha: 0.56, n: Math.round(rx0 * ry0 / 36), len: 11, angle: -0.95 + rnd(seed + 75, i) * 0.5, seed: seed + i * 13 });
        scribble(ctx, gx0, gy0, rx0 * 0.6, ry0 * 0.6, { color: C.grassDark, alpha: 0.3, n: Math.round(rx0 * ry0 / 130), len: 8, angle: -1.2, seed: seed + i * 29 });
      }
      const clumps = Math.max(24, Math.round((p.w * p.h) / 7000));
      for (let i = 0; i < clumps; i++) {
        const gx = x + rnd(seed, i) * p.w, gy = y + rnd(seed + 5, i) * p.h;
        const blades = 3 + Math.floor(rnd(seed + 9, i) * 3);
        for (let k = 0; k < blades; k++) {
          const bx = gx + (k - blades / 2) * 3.2, hgt = 7 + rnd(seed + k, i) * 7;
          inkPath(ctx, [[bx, gy], [bx + (k - blades / 2) * 1.6, gy - hgt * 0.6], [bx + (k - blades / 2) * 3.4, gy - hgt]],
            { color: GREEN, width: W.shade + 0.2, seed: seed + i * 7 + k, amp: 0.5, alpha: 0.75 });
        }
        if (rnd(seed + 31, i) > 0.86) {
          const fx = gx + 4, fy = gy - 11;
          ctx.fillStyle = rnd(seed + 41, i) > 0.5 ? RED : GOLD;
          ctx.beginPath(); ctx.arc(fx, fy, 1.8, 0, 7); ctx.fill();
          inkLine(ctx, fx, fy + 1.5, fx - 1, gy, { color: GREEN, width: W.shade, amp: 0.4, alpha: 0.7, seed: seed + i });
        }
      }
      break;
    }
    case 'bush': {
      // built the same way as a tree crown: opaque clumps, back to front, lit from the top left
      groundShade(ctx, x + w * 0.56, y + h, w * 0.66, seed, 0.16);
      const gs = SHADE[(['summer', 'deep', 'olive'] as const)[seed % 3]!]!;
      type BC = { x: number; y: number; r: number; ry: number; lit: number };
      const bc: BC[] = [];
      const bn = Math.max(5, Math.round(w / 34));
      for (let m = 0; m < bn; m++) {
        const a = Math.PI + (m / (bn - 1)) * Math.PI + (rnd(seed, m) - 0.5) * 0.5;
        const d = m === 0 ? 0.1 : 0.52;
        const px = x + w * 0.5 + Math.cos(a) * w * 0.32 * d * 2;
        const py = y + h * 0.58 + Math.sin(a) * h * 0.3 * d * 2;
        const r = w * (m === 0 ? 0.3 : 0.2 + rnd(seed + 3, m) * 0.08);
        bc.push({ x: px, y: py, r, ry: r * (h / w) * 0.95, lit: 0.6 - Math.cos(a) * d * 0.4 - Math.sin(a) * d * 0.5 });
      }
      bc.sort((a, b) => (a.y - a.ry) - (b.y - b.ry));
      for (let i = 0; i < bc.length; i++) {
        const c = bc[i]!;
        const lobes = 7 + (i % 3), steps = lobes * 3;
        const pts: [number, number][] = [];
        for (let q = 0; q < steps; q++) {
          const t = (q / steps) * Math.PI * 2;
          const sc = 1 + Math.cos(t * lobes) * 0.18 + (rnd(seed + i * 11, q) - 0.5) * 0.08;
          pts.push([c.x + Math.cos(t) * c.r * sc, c.y + Math.sin(t) * c.ry * sc]);
        }
        pencil(ctx, pts, {
          color: gs.line, width: i === bc.length - 1 ? W.edge : W.detail, seed: seed + i * 5, amp: 0.45, smooth: true,
          fill: c.lit > 0.58 ? gs.light : c.lit > 0.33 ? gs.mid : gs.dark, fillAlpha: 0.97,
          shade: c.lit < 0.33 ? gs.dark : undefined, shadeAlpha: 0.16, gap: 4, angle: -0.6,
        });
        const bm = Math.max(5, Math.round((c.r * c.ry) / 260));
        for (let q = 0; q < bm; q++) {
          const a2 = rnd(seed + i * 17, q) * Math.PI * 2, d2 = Math.sqrt(rnd(seed + i * 19, q)) * 0.7;
          const lx = c.x + Math.cos(a2) * c.r * d2, ly = c.y + Math.sin(a2) * c.ry * d2;
          inkPath(ctx, [[lx - 2.6, ly + 1.4], [lx, ly - 2.6], [lx + 2.6, ly + 1.4]],
            { color: gs.line, width: W.shade, seed: seed + i * 7 + q, amp: 0.3, alpha: 0.42 });
        }
      }
      // leaves breaking the outline, and berries on some bushes
      const rimN = Math.max(14, Math.round(w / 9));
      for (let i = 0; i < rimN; i++) {
        const a = Math.PI * (1 + i / (rimN - 1)) + (rnd(seed + 31, i) - 0.5) * 0.3;
        const lx = x + w * 0.5 + Math.cos(a) * w * 0.47, ly = y + h * 0.58 + Math.sin(a) * h * 0.44;
        const la = a + 0.5, len = 5 + rnd(seed + 37, i) * 3;
        const tx = lx + Math.cos(la) * len, ty = ly + Math.sin(la) * len;
        const nx = -Math.sin(la) * len * 0.3, ny = Math.cos(la) * len * 0.3;
        inkPath(ctx, [[lx, ly], [lx + (tx - lx) * 0.45 + nx, ly + (ty - ly) * 0.45 + ny], [tx, ty], [lx + (tx - lx) * 0.45 - nx, ly + (ty - ly) * 0.45 - ny]],
          { color: gs.line, width: W.shade, seed: seed + i, amp: 0.25, close: true, alpha: 0.8, fill: gs.mid });
      }
      if (seed % 3 === 1) for (let i = 0; i < 7; i++) {
        ctx.fillStyle = RED; ctx.globalAlpha = 0.85;
        ctx.beginPath(); ctx.arc(x + w * (0.22 + rnd(seed + 8, i) * 0.6), y + h * (0.32 + rnd(seed + 9, i) * 0.42), 2.4, 0, 7); ctx.fill();
        ctx.globalAlpha = 1;
        inkEllipse(ctx, x + w * (0.22 + rnd(seed + 8, i) * 0.6), y + h * (0.32 + rnd(seed + 9, i) * 0.42), 2.6, 2.6, { color: '#8d2020', width: W.shade, amp: 0.2, alpha: 0.7, seed: seed + i });
      }
      break;
    }
    case 'stump': {
      // a cut trunk: an elliptical top with rings, a tapered side, roots spreading at the base
      groundShade(ctx, x + w * 0.55, y + h, w * 0.62, seed, 0.13);
      const top = y + h * 0.3, ry = h * 0.22, rx2 = w * 0.46, cxs = x + w * 0.5;
      pencil(ctx, [[cxs - rx2, top], [cxs - rx2 * 0.86, y + h * 0.82], [cxs + rx2 * 0.86, y + h * 0.82], [cxs + rx2, top]],
        { color: PENCIL, width: W.contour, seed, amp: 0.7, fill: C.trunk, fillAlpha: 0.6, shade: C.trunkDark, shadeAlpha: 0.3, gap: 3.2, angle: -1.42 });
      inkEllipse(ctx, cxs, top, rx2, ry, { color: PENCIL, width: W.edge, amp: 0.6, seed: seed + 1, fill: 'rgba(214,180,132,0.95)' });
      for (let i = 1; i <= 3; i++) {
        const f = 1 - i * 0.24;
        inkEllipse(ctx, cxs + i * 0.8, top + i * 0.5, rx2 * f, ry * f, { color: C.trunkDark, width: W.shade, amp: 0.45, alpha: 0.5, seed: seed + i * 7 });
      }
      for (const sdir of [-1, 1] as const) {
        inkPath(ctx, [[cxs + sdir * rx2 * 0.8, y + h * 0.8], [cxs + sdir * rx2 * 1.2, y + h * 0.92], [cxs + sdir * rx2 * 1.35, y + h]],
          { color: PENCIL, width: W.detail, seed: seed + 3 + sdir, amp: 0.5, alpha: 0.85 });
      }
      for (let i = 0; i < 4; i++) {
        const gx2 = x + w * (0.1 + rnd(seed + 9, i) * 0.8);
        inkPath(ctx, [[gx2, y + h], [gx2 + 2, y + h - 6], [gx2 + 4, y + h]], { color: C.grassDark, width: W.shade, seed: seed + 11 + i, amp: 0.3, alpha: 0.6 });
      }
      break;
    }
    case 'flowers': {
      scribble(ctx, x + w * 0.5, y + h * 0.6, w * 0.5, h * 0.42, { color: C.grass, alpha: 0.38, n: 26, len: 10, angle: -1.1, seed });
      for (let i = 0; i < 12; i++) {
        const fx = x + rnd(seed + 1, i) * w, fy = y + h * 0.25 + rnd(seed + 2, i) * h * 0.6;
        const col = [RED, GOLD, '#b060b0', '#ffffff'][Math.floor(rnd(seed + 3, i) * 4)]!;
        inkLine(ctx, fx, fy + 7, fx - 1, fy, { color: C.grassDark, width: W.shade, amp: 0.4, alpha: 0.7, seed: seed + i });
        for (let k = 0; k < 5; k++) {
          const a2 = (k / 5) * Math.PI * 2;
          ctx.fillStyle = col; ctx.globalAlpha = 0.8;
          ctx.beginPath(); ctx.arc(fx + Math.cos(a2) * 1.9, fy + Math.sin(a2) * 1.9, 1.25, 0, 7); ctx.fill();
        }
        ctx.globalAlpha = 1;
        ctx.fillStyle = GOLD; ctx.beginPath(); ctx.arc(fx, fy, 1, 0, 7); ctx.fill();
      }
      break;
    }
    case 'barrel': {
      groundShade(ctx, x + w * 0.55, y + h, w * 0.6, seed, 0.13);
      pencil(ctx, [[x + w * 0.12, y + h * 0.08], [x + w * 0.88, y + h * 0.08], [x + w, y + h * 0.5], [x + w * 0.88, y + h], [x + w * 0.12, y + h], [x, y + h * 0.5]],
        { color: PENCIL, width: W.contour, seed, amp: 0.7, fill: C.timber, fillAlpha: 0.6, shade: C.trunkDark, shadeAlpha: 0.24, gap: 3.4, angle: -1.42 });
      inkEllipse(ctx, x + w * 0.5, y + h * 0.1, w * 0.38, h * 0.09, { color: PENCIL, width: W.edge, amp: 0.4, seed: seed + 1, fill: '#c8a878' });
      for (const f of [0.3, 0.72]) inkLine(ctx, x + w * 0.02, y + h * f, x + w * 0.98, y + h * f, { color: PENCIL, width: W.detail, amp: 0.5, alpha: 0.85, seed: seed + f * 20 });
      break;
    }
    case 'stall': {
      // a market stall: four posts, a striped awning, a counter with things to sell on it
      const postW = w * 0.035, top = y + h * 0.1, counter = y + h * 0.62, base2 = y + h;
      const dx2 = w * 0.1, dy2 = -h * 0.07;
      groundShade(ctx, x + w * 0.5, base2, w * 0.62, seed);
      // back posts first, then the awning, then the counter in front of them
      for (const f of [0.14, 0.86]) {
        const px = x + w * f + dx2;
        pencil(ctx, [[px - postW, top + dy2], [px + postW, top + dy2], [px + postW, base2 + dy2], [px - postW, base2 + dy2]],
          { color: PENCIL, width: W.detail, seed: seed + f * 10, amp: 0.4, fill: C.trunk, fillAlpha: 0.5 });
      }
      // the awning, striped, sloping towards the viewer
      const stripes = 7;
      for (let i = 0; i < stripes; i++) {
        const f0 = i / stripes, f1 = (i + 1) / stripes;
        pencil(ctx, [[x + w * f0 + dx2, top + dy2], [x + w * f1 + dx2, top + dy2], [x + w * f1, top + h * 0.14], [x + w * f0, top + h * 0.14]],
          { color: INK, width: W.shade, seed: seed + i, amp: 0.3, fill: i % 2 ? C.clothBlue : C.canvasWhite, fillAlpha: 0.9, alpha: 0.8 });
      }
      // the scalloped hem
      const scallops = 8;
      for (let i = 0; i < scallops; i++) {
        const f = i / scallops, cxs = x + w * (f + 0.5 / scallops);
        inkPath(ctx, [[x + w * f, top + h * 0.14], [cxs, top + h * 0.2], [x + w * (f + 1 / scallops), top + h * 0.14]],
          { color: INK, width: W.detail, seed: seed + 20 + i, amp: 0.3, close: true, alpha: 0.85, fill: i % 2 ? C.clothBlue : C.canvasWhite });
      }
      inkLine(ctx, x, top + h * 0.14, x + w, top + h * 0.14, { color: INK, width: W.edge, amp: 0.5, seed: seed + 3 });
      // front posts
      for (const f of [0.06, 0.94]) {
        const px = x + w * f;
        pencil(ctx, [[px - postW, top + h * 0.16], [px + postW, top + h * 0.16], [px + postW, base2], [px - postW, base2]],
          { color: PENCIL, width: W.edge, seed: seed + 5 + f * 10, amp: 0.4, fill: C.trunk, fillAlpha: 0.6, shade: C.trunkDark, shadeAlpha: 0.22, gap: 3, angle: -1.44 });
      }
      // the counter, and its front board
      pencil(ctx, [[x + w * 0.02, counter], [x + w * 0.98, counter], [x + w * 0.9, counter + h * 0.06], [x + w * 0.1, counter + h * 0.06]],
        { color: PENCIL, width: W.edge, seed: seed + 7, amp: 0.4, fill: '#c8a878', fillAlpha: 0.9 });
      pencil(ctx, [[x + w * 0.1, counter + h * 0.06], [x + w * 0.9, counter + h * 0.06], [x + w * 0.9, base2 - h * 0.04], [x + w * 0.1, base2 - h * 0.04]],
        { color: PENCIL, width: W.detail, seed: seed + 8, amp: 0.4, fill: C.timber, fillAlpha: 0.55, shade: C.trunkDark, shadeAlpha: 0.2, gap: 4, angle: -1.44 });
      // goods on the counter: two ink bottles and a small stack of paper
      for (let i = 0; i < 2; i++) {
        const bx = x + w * (0.18 + i * 0.14), by = counter;
        pencil(ctx, [[bx - 4, by], [bx + 4, by], [bx + 3, by - 9], [bx - 3, by - 9]],
          { color: INK, width: W.detail, seed: seed + 30 + i, amp: 0.3, fill: C.water, fillAlpha: 0.7 });
        inkLine(ctx, bx - 1.5, by - 9, bx + 1.5, by - 12, { color: INK, width: W.shade, amp: 0.3, seed: seed + 40 + i });
      }
      for (let i = 0; i < 3; i++) {
        const py = counter - 2 - i * 2.4;
        inkPath(ctx, [[x + w * 0.58, py], [x + w * 0.78, py - 1], [x + w * 0.78, py + 2], [x + w * 0.58, py + 2.4]],
          { color: PENCIL_SOFT, width: W.shade, seed: seed + 50 + i, amp: 0.3, close: true, alpha: 0.8, fill: C.canvasWhite });
      }
      // a hanging sign with an ink bottle on it
      const sx2 = x + w * 0.82, sy2 = top + h * 0.22;
      inkLine(ctx, sx2, top + h * 0.16, sx2, sy2, { color: PENCIL, width: W.shade, amp: 0.3, seed: seed + 9 });
      pencil(ctx, [[sx2 - w * 0.09, sy2], [sx2 + w * 0.09, sy2], [sx2 + w * 0.09, sy2 + h * 0.16], [sx2 - w * 0.09, sy2 + h * 0.16]],
        { color: INK, width: W.detail, seed: seed + 10, amp: 0.4, fill: C.canvasWhite, fillAlpha: 0.95 });
      penText(ctx, 'INK', sx2, sy2 + h * 0.11, { size: 10, align: 'center', color: INK, seed: seed + 11 });
      break;
    }
    case 'crate': {
      // a box seen slightly from above: top face, front face, planks and corner nails
      const d = h * 0.34;
      groundShade(ctx, x + w * 0.55, y + h, w * 0.6, seed, 0.12);
      inkPath(ctx, [[x, y + d], [x + w * 0.22, y], [x + w, y], [x + w * 0.78, y + d]],
        { color: PENCIL, width: W.edge, seed: seed + 5, amp: 0.6, close: true, fill: 'rgba(200,164,116,0.85)' });
      inkPath(ctx, [[x, y + d], [x + w * 0.78, y + d], [x + w * 0.78, y + h], [x, y + h]],
        { color: PENCIL, width: W.contour, seed, amp: 0.7, close: true, fill: 'rgba(216,183,136,0.9)' });
      inkPath(ctx, [[x + w * 0.78, y + d], [x + w, y], [x + w, y + h * 0.72], [x + w * 0.78, y + h]],
        { color: PENCIL, width: W.edge, seed: seed + 1, amp: 0.6, close: true, fill: 'rgba(168,131,86,0.9)' });
      for (const k of [0.34, 0.66]) inkLine(ctx, x + 2, y + d + (h - d) * k, x + w * 0.78 - 2, y + d + (h - d) * k, { color: PENCIL_SOFT, width: W.detail, amp: 0.5, alpha: 0.7, seed: seed + 10 + k * 10 });
      inkLine(ctx, x + 3, y + d + 3, x + w * 0.76, y + h - 3, { color: PENCIL_SOFT, width: W.detail, amp: 0.7, alpha: 0.55, seed: seed + 2 });
      for (const [nx, ny] of [[x + 4, y + d + 4], [x + w * 0.74, y + d + 4], [x + 4, y + h - 4], [x + w * 0.74, y + h - 4]] as const) { ctx.fillStyle = PENCIL; ctx.beginPath(); ctx.arc(nx, ny, 1.1, 0, 7); ctx.fill(); }
      hatch(ctx, x + w * 0.78, y, w * 0.22, h, { gap: 3.4, angle: -1.2, alpha: 0.3, seed: seed + 4 });
      break;
    }
  }
}

// ---------- buildings ----------
// A cottage drawn the way the reference page draws one: the gable facing you, the long wall
// running back and to the right, and every surface coloured under its own ink line.
// Three cottages side by side must not be the same cottage three times. Everything a village
// builder would actually vary is varied here, off the prop's own seed.
const COTTAGE: { roof: string; roofDark: string; wall: string; wallDark: string; door: string; slope: number }[] = [
  { roof: '#c2553d', roofDark: '#95382a', wall: '#f2ead7', wallDark: '#cdbd99', door: '#8a6340', slope: 1 },
  { roof: '#a2623c', roofDark: '#79452a', wall: '#ece3cb', wallDark: '#c6b593', door: '#5f452e', slope: 1.12 },
  { roof: '#7d8a99', roofDark: '#57626e', wall: '#f4f0e4', wallDark: '#cfc8b6', door: '#6b4e2e', slope: 0.9 },
  { roof: '#b8814a', roofDark: '#8a5c31', wall: '#efe4cd', wallDark: '#c9b78f', door: '#7a4f3a', slope: 1.05 },
];

function drawCottage(ctx: CanvasRenderingContext2D, p: ZoneProp) {
  const { x, y, w, h, seed } = p;
  const V = COTTAGE[seed % COTTAGE.length]!;
  const base = y + h;
  const fw = w * 0.66;                       // the gable wall you see head on
  const dx = w * 0.34, dy = -h * 0.17;       // the direction the building runs away from you
  const wallTop = y + h * 0.44;
  const apex = y + h * (0.1 - 0.05 * V.slope);
  const ridgeX = x + fw * 0.5;
  const ov = w * 0.07;                       // the roof overhangs the walls
  groundShade(ctx, x + w * 0.5, base + 2, w * 0.62, seed);

  // --- the long side wall, in shadow
  pencil(ctx, [[x + fw, wallTop], [x + fw + dx, wallTop + dy], [x + fw + dx, base + dy], [x + fw, base]],
    { color: INK, width: W.edge, seed: seed + 1, amp: 0.6, fill: V.wall, fillAlpha: 0.9, shade: V.wallDark, shadeAlpha: 0.4, gap: 4, angle: -1.2 });
  // --- the gable wall, facing the light
  pencil(ctx, [[x, wallTop], [x + fw, wallTop], [x + fw, base], [x, base]],
    { color: INK, width: W.contour, seed, amp: 0.6, fill: V.wall, fillAlpha: 0.97 });

  // stone footing under both walls
  const foot = base - h * 0.16;
  const courses = Math.max(2, Math.round((base - foot) / 13));
  for (let c = 0; c < courses; c++) {
    const y0 = foot + ((base - foot) * c) / courses, y1 = foot + ((base - foot) * (c + 1)) / courses;
    const per = Math.max(3, Math.round(fw / 26));
    for (let i = 0; i < per; i++) {
      const off = (c % 2) * 0.5;
      const sx = x + 2 + ((i + off) * (fw - 4)) / per, sw = (fw - 4) / per - 2;
      if (sx + sw > x + fw - 2) continue;
      inkPath(ctx, [[sx, y0 + (i % 2) * 1.5], [sx + sw, y0 + ((i + 1) % 2) * 1.5], [sx + sw, y1], [sx, y1]],
        { color: PENCIL_SOFT, width: W.shade, seed: seed + 40 + c * 9 + i, amp: 0.5, close: true, alpha: 0.5, fill: '#cfc8b6' });
    }
  }
  inkPath(ctx, [[x + fw, foot], [x + fw + dx, foot + dy], [x + fw + dx, base + dy], [x + fw, base]],
    { color: PENCIL_SOFT, width: W.shade, seed: seed + 46, amp: 0.5, close: true, alpha: 0.6, fill: C.stoneDark });

  hatch(ctx, x + 2, wallTop + 3, fw - 4, foot - wallTop - 4, { color: V.wallDark, gap: 7, angle: -1.2, alpha: 0.13, seed: seed + 51 });
  for (let i = 0; i < 2; i++) {
    const crx = x + fw * (0.2 + i * 0.45), cry = wallTop + (foot - wallTop) * (0.25 + i * 0.3);
    inkPath(ctx, [[crx, cry], [crx + 5, cry + 9], [crx + 2, cry + 17], [crx + 7, cry + 24]],
      { color: V.wallDark, width: W.shade, seed: seed + 55 + i, amp: 0.6, alpha: 0.45 });
  }

  // exposed timber framing on the gable, the way a half-timbered cottage is built
  const tim = { color: C.timber, width: W.detail, alpha: 0.85 };
  inkLine(ctx, x + 3, wallTop + 3, x + fw - 3, wallTop + 3, { ...tim, amp: 0.5, seed: seed + 2 });
  inkLine(ctx, x + 3, foot - 3, x + fw - 3, foot - 3, { ...tim, amp: 0.5, seed: seed + 3 });
  for (const f of [0.02, 0.98]) inkLine(ctx, x + fw * f, wallTop + 3, x + fw * f, foot - 3, { ...tim, amp: 0.5, seed: seed + 4 + f * 10 });
  inkLine(ctx, x + fw * 0.28, wallTop + 4, x + fw * 0.44, foot - 4, { ...tim, amp: 0.6, alpha: 0.6, seed: seed + 6 });

  // --- roof: the front slope you see, the long slope running back, shingle courses on both
  const eaveL = x - ov, eaveR = x + fw + ov;
  const ridgeBack = { x: ridgeX + dx, y: apex + dy };
  pencil(ctx, [[eaveL, wallTop + 2], [ridgeX, apex], [eaveR, wallTop + 2]],
    { color: INK, width: W.contour, seed: seed + 7, amp: 0.6, fill: V.roof, fillAlpha: 0.94 });
  pencil(ctx, [[eaveR, wallTop + 2], [ridgeX, apex], [ridgeBack.x, ridgeBack.y], [eaveR + dx, wallTop + 2 + dy]],
    { color: INK, width: W.edge, seed: seed + 8, amp: 0.6, fill: V.roofDark, fillAlpha: 0.9, shade: V.roofDark, shadeAlpha: 0.25, gap: 4.5, angle: -1.1 });
  // shingle courses on the gable slope
  const rows = Math.max(4, Math.min(14, Math.round((wallTop - apex) / 16)));
  for (let r = 1; r <= rows; r++) {
    const k = r / (rows + 1);
    const ly = wallTop + 2 - (wallTop + 2 - apex) * k;
    const lx0 = eaveL + (ridgeX - eaveL) * k, lx1 = eaveR - (eaveR - ridgeX) * k;
    const n = Math.max(3, Math.round((lx1 - lx0) / 13));
    const drop = (wallTop - apex) / (rows + 1) * 0.8;
    for (let i = 0; i < n; i++) {
      const sx = lx0 + ((lx1 - lx0) * i) / n, ex = lx0 + ((lx1 - lx0) * (i + 1)) / n;
      inkPath(ctx, [[sx, ly], [ex, ly], [ex - 1.6, ly + drop], [sx + 1.6, ly + drop]],
        { color: V.roofDark, width: W.shade, seed: seed + r * 13 + i, amp: 0.3, close: true, alpha: 0.6 });
    }
  }
  // courses running back along the long slope
  for (let r = 1; r <= rows; r++) {
    const k = r / (rows + 1);
    const ax = eaveR + (ridgeX - eaveR) * k, ay = wallTop + 2 + (apex - (wallTop + 2)) * k;
    inkLine(ctx, ax, ay, ax + dx, ay + dy, { color: V.roofDark, width: W.shade, amp: 0.5, alpha: 0.45, seed: seed + 60 + r });
  }
  inkLine(ctx, ridgeX, apex, ridgeBack.x, ridgeBack.y, { color: INK, width: W.edge, amp: 0.5, seed: seed + 9 });
  inkLine(ctx, eaveL, wallTop + 2, eaveR, wallTop + 2, { color: INK, width: W.edge, amp: 0.5, seed: seed + 10 });

  // --- chimney, with smoke going up the page
  const ct = 0.62;                            // how far back along the roof it stands
  const rmx = ridgeX + dx * ct, rmy = apex + dy * ct;                    // ridge at that point
  const emx = eaveR + dx * ct, emy = wallTop + 2 + dy * ct;              // eave at that point
  const chx = rmx + (emx - rmx) * 0.3, chy = rmy + (emy - rmy) * 0.3;    // a point on the slope
  const cw = w * 0.05, ch = h * 0.2, cd = w * 0.042, cdy2 = -h * 0.038;
  // the shaft, sunk into the roof so it does not look stuck on
  pencil(ctx, [[chx - cw, chy + h * 0.05], [chx + cw, chy + h * 0.05], [chx + cw, chy - ch], [chx - cw, chy - ch]],
    { color: PENCIL, width: W.edge, seed: seed + 11, amp: 0.45, fill: '#c08a72', fillAlpha: 0.85 });
  // the side of the shaft, running back with the rest of the building
  pencil(ctx, [[chx + cw, chy + h * 0.04], [chx + cw + cd, chy + h * 0.04 + cdy2], [chx + cw + cd, chy - ch + cdy2], [chx + cw, chy - ch]],
    { color: PENCIL, width: W.detail, seed: seed + 12, amp: 0.4, fill: '#8d6152', fillAlpha: 0.85 });
  // brick courses
  for (let i = 1; i < 5; i++) {
    const by2 = chy - ch + (ch + h * 0.05) * (i / 5);
    inkLine(ctx, chx - cw + 0.6, by2, chx + cw - 0.6, by2, { color: PENCIL_SOFT, width: W.shade, amp: 0.35, alpha: 0.55, seed: seed + 80 + i });
    if (i % 2) inkLine(ctx, chx, by2, chx, by2 - (ch + h * 0.05) / 5, { color: PENCIL_SOFT, width: W.shade, amp: 0.3, alpha: 0.4, seed: seed + 90 + i });
  }
  // the cap, a slab wider than the shaft
  pencil(ctx, [[chx - cw * 1.28, chy - ch], [chx + cw * 1.28, chy - ch], [chx + cw * 1.28 + cd, chy - ch + cdy2], [chx - cw * 1.28 + cd, chy - ch + cdy2]],
    { color: PENCIL, width: W.detail, seed: seed + 13, amp: 0.35, fill: C.stoneDark, fillAlpha: 0.9 });
  inkLine(ctx, chx - cw * 1.28, chy - ch, chx + cw * 1.28, chy - ch, { color: PENCIL, width: W.detail, amp: 0.3, seed: seed + 14 });
  // smoke: one curl that wanders upwards and thins out, not a stack of rings
  const puff: [number, number][] = [];
  for (let i = 0; i <= 9; i++) {
    const f = i / 9;
    puff.push([chx + Math.sin(f * 4.2 + seed * 0.7) * w * (0.02 + f * 0.1), chy - ch - h * (0.04 + f * 0.42)]);
  }
  for (let k = 0; k < 2; k++) {
    inkPath(ctx, puff.map(([px, py]) => [px + k * w * 0.02, py + k * h * 0.02] as [number, number]),
      { color: PENCIL_SOFT, width: W.shade, seed: seed + 70 + k, amp: 1.2, alpha: 0.26 - k * 0.1 });
  }
  for (let i = 2; i < 9; i += 2) {
    const [px, py] = puff[i]!;
    inkEllipse(ctx, px, py, w * (0.016 + i * 0.006), h * (0.012 + i * 0.004),
      { color: PENCIL_SOFT, width: W.shade, amp: 0.35, alpha: 0.2 - i * 0.018, seed: seed + 80 + i });
  }

  // --- door under a shallow arch, with a step
  const dw = fw * 0.3, dxd = x + fw * 0.35, dyd = wallTop + (base - wallTop) * 0.34;
  pencil(ctx, [[dxd, dyd + dw * 0.18], [dxd + dw * 0.5, dyd], [dxd + dw, dyd + dw * 0.18], [dxd + dw, foot], [dxd, foot]],
    { color: INK, width: W.edge, seed: seed + 13, amp: 0.4, fill: V.door, fillAlpha: 0.85, shade: C.trunkDark, shadeAlpha: 0.22, gap: 3, angle: -1.45 });
  for (const k of [0.34, 0.67]) inkLine(ctx, dxd + dw * k, dyd + dw * 0.22, dxd + dw * k, foot - 1.5, { color: C.trunkDark, width: W.shade, amp: 0.3, alpha: 0.6, seed: seed + 14 + k * 10 });
  ctx.fillStyle = PENCIL; ctx.beginPath(); ctx.arc(dxd + dw * 0.82, (dyd + foot) / 2 + 2, 1.5, 0, 7); ctx.fill();
  inkPath(ctx, [[dxd - 3, base], [dxd + dw + 3, base], [dxd + dw + 1, foot + 1], [dxd - 1, foot + 1]],
    { color: PENCIL_SOFT, width: W.shade, seed: seed + 15, amp: 0.4, close: true, alpha: 0.7, fill: C.stone });

  // --- window with shutters and a flower box
  const wx = x + fw * 0.08, wy = wallTop + (base - wallTop) * 0.24, ww = fw * 0.2, wh = ww * 1.05;
  pencil(ctx, [[wx, wy], [wx + ww, wy], [wx + ww, wy + wh], [wx, wy + wh]],
    { color: INK, width: W.edge, seed: seed + 16, amp: 0.4, fill: C.water, fillAlpha: 0.5 });
  inkLine(ctx, wx + ww / 2, wy + 1, wx + ww / 2, wy + wh - 1, { color: INK, width: W.detail, amp: 0.3, seed: seed + 17 });
  inkLine(ctx, wx + 1, wy + wh / 2, wx + ww - 1, wy + wh / 2, { color: INK, width: W.detail, amp: 0.3, seed: seed + 18 });
  inkLine(ctx, wx + 2, wy + wh * 0.44, wx + ww * 0.44, wy + 2, { color: '#ffffff', width: W.detail, amp: 0.3, alpha: 0.85, seed: seed + 19 });
  for (const sdir of [-1, 1] as const) {
    const sxw = sdir < 0 ? wx - ww * 0.3 : wx + ww;
    inkPath(ctx, [[sxw, wy - 1], [sxw + ww * 0.3, wy - 1], [sxw + ww * 0.3, wy + wh + 1], [sxw, wy + wh + 1]],
      { color: INK_SOFT, width: W.detail, seed: seed + 20 + sdir, amp: 0.4, close: true, alpha: 0.9, fill: 'rgba(125,155,201,0.75)' });
  }
  inkPath(ctx, [[wx - 3, wy + wh + 1], [wx + ww + 3, wy + wh + 1], [wx + ww + 1, wy + wh + 7], [wx - 1, wy + wh + 7]],
    { color: C.trunkDark, width: W.detail, seed: seed + 22, amp: 0.4, close: true, fill: C.timber });
  for (let i = 0; i < 5; i++) {
    const fx = wx - 1 + i * (ww + 2) / 4.4;
    ctx.fillStyle = i % 2 ? RED : GOLD; ctx.globalAlpha = 0.85;
    ctx.beginPath(); ctx.arc(fx, wy + wh + 0.5, 1.7, 0, 7); ctx.fill(); ctx.globalAlpha = 1;
  }

  // a window on the long wall, seen at an angle
  const swx = x + fw + dx * 0.34, swy = wallTop + (base - wallTop) * 0.26 + dy * 0.34;
  const sww = fw * 0.16, swh = sww * 1.05;
  pencil(ctx, [[swx, swy], [swx + sww, swy + dy * 0.1], [swx + sww, swy + swh + dy * 0.1], [swx, swy + swh]],
    { color: INK, width: W.detail, seed: seed + 26, amp: 0.4, fill: C.water, fillAlpha: 0.45 });
  inkLine(ctx, swx + sww / 2, swy + 1, swx + sww / 2, swy + swh - 1, { color: INK, width: W.shade, amp: 0.3, seed: seed + 27 });

  // a creeper climbing the near corner: one wandering stem with leaves off both sides
  const vine: [number, number][] = [];
  for (let i = 0; i <= 10; i++) {
    const f = i / 10;
    vine.push([x + fw - 6 - Math.sin(f * 5.5 + seed) * fw * 0.06, base - h * 0.18 - f * (base - wallTop) * 0.78]);
  }
  inkPath(ctx, vine, { color: C.grassDark, width: W.shade, seed: seed + 59, amp: 0.4, alpha: 0.7 });
  for (let i = 1; i < vine.length; i++) {
    const [vx, vy] = vine[i]!;
    const sdir = i % 2 ? 1 : -1;
    const lx = vx + sdir * 5, ly = vy - 1;
    inkPath(ctx, [[vx, vy], [lx, ly - 3.5], [lx + sdir * 3, ly + 1], [vx, vy + 1]],
      { color: C.grassDark, width: W.shade, seed: seed + 60 + i, amp: 0.3, close: true, alpha: 0.75, fill: C.grass });
  }

  // a bush tucked against the wall, and a creeper up the corner
  for (let i = 0; i < 6; i++) {
    const bx = x + fw * (0.72 + rnd(seed + 30, i) * 0.22), by = base - 2 - rnd(seed + 31, i) * h * 0.08;
    inkPath(ctx, [[bx, by], [bx + 3, by - 5], [bx + 6, by]], { color: C.grassDark, width: W.shade, seed: seed + 32 + i, amp: 0.35, alpha: 0.6 });
  }
}

// ---------- trees ----------
// Ten trees that are actually different trees, not one tree with different jitter: the trunk, the
// branch pattern, the canopy shape and the foliage marks all change per variant.
interface TreeKind {
  trunk: 'straight' | 'lean' | 'gnarled' | 'slim';
  bark: 'ticks' | 'grain' | 'plates' | 'smooth';
  canopy: 'round' | 'tall' | 'conic' | 'sparse' | 'wide';
  foliage: 'leaf' | 'needle' | 'scallop';
  masses: number;
  roots: boolean;
  litter: number;
  palette: 'summer' | 'deep' | 'olive' | 'pine';
}
const TREE_KINDS: TreeKind[] = [
  { trunk: 'straight', bark: 'ticks', canopy: 'round', foliage: 'leaf', masses: 4, roots: false, litter: 3, palette: 'summer' },
  { trunk: 'lean', bark: 'grain', canopy: 'wide', foliage: 'leaf', masses: 5, roots: true, litter: 4, palette: 'summer' },
  { trunk: 'gnarled', bark: 'plates', canopy: 'sparse', foliage: 'leaf', masses: 3, roots: true, litter: 5, palette: 'olive' },
  { trunk: 'slim', bark: 'smooth', canopy: 'tall', foliage: 'scallop', masses: 3, roots: false, litter: 2, palette: 'deep' },
  { trunk: 'straight', bark: 'grain', canopy: 'conic', foliage: 'needle', masses: 5, roots: false, litter: 2, palette: 'pine' },
  { trunk: 'lean', bark: 'ticks', canopy: 'conic', foliage: 'needle', masses: 4, roots: true, litter: 3, palette: 'pine' },
  { trunk: 'gnarled', bark: 'plates', canopy: 'wide', foliage: 'scallop', masses: 4, roots: true, litter: 6, palette: 'deep' },
  { trunk: 'slim', bark: 'smooth', canopy: 'round', foliage: 'leaf', masses: 3, roots: false, litter: 2, palette: 'summer' },
  { trunk: 'straight', bark: 'plates', canopy: 'tall', foliage: 'leaf', masses: 4, roots: false, litter: 3, palette: 'summer' },
  { trunk: 'lean', bark: 'grain', canopy: 'sparse', foliage: 'scallop', masses: 3, roots: true, litter: 4, palette: 'olive' },
];

function drawTree(ctx: CanvasRenderingContext2D, p: ZoneProp) {
  const { x, y, w, h, seed } = p;
  const k = TREE_KINDS[seed % TREE_KINDS.length]!;
  const cx = x + w / 2, base = y + h;
  const lean = k.trunk === 'lean' ? (rnd(seed, 1) - 0.5) * w * 0.2 : 0;
  const tw = w * (k.trunk === 'slim' ? 0.06 : k.trunk === 'gnarled' ? 0.105 : 0.08);
  const crownY = y + h * (k.canopy === 'tall' ? 0.4 : k.canopy === 'conic' ? 0.68 : 0.52);
  const shade: Greens = SHADE[k.palette];

  // the shadow the tree throws, offset away from the light
  groundShade(ctx, cx + w * 0.14 + lean * 0.3, base - 1, w * 0.5, seed, 0.2);

  drawTrunk(ctx, { cx, base, crownY, tw, lean, h, w, seed, kind: k });

  if (k.canopy === 'conic') drawConifer(ctx, { cx: cx + lean, crownY, w, h, seed, shade });
  else drawCrown(ctx, { cx: cx + lean, crownY, w, h, seed, k, shade });

  // what has fallen off it
  for (let i = 0; i < k.litter; i++) {
    const lx = cx + (rnd(seed + 21, i) - 0.5) * w * 1.05, ly = base + 1 + rnd(seed + 22, i) * 7;
    inkPath(ctx, [[lx, ly], [lx + 3.5, ly - 2], [lx + 6.5, ly + 0.5]], { color: shade.line, width: W.shade, seed: seed + i + 40, amp: 0.3, alpha: 0.45 });
  }
}

/** the three greens a tree is drawn with: lit, local, and the shadow inside the foliage */
interface Greens { light: string; mid: string; dark: string; line: string }
const SHADE: Record<TreeKind['palette'], Greens> = {
  summer: { light: '#9cc26a', mid: '#6d9a4e', dark: '#46703a', line: '#3b6030' },
  deep:   { light: '#7fae5d', mid: '#54853f', dark: '#3a6132', line: '#2f5128' },
  olive:  { light: '#a8bd63', mid: '#7d9a44', dark: '#566f2e', line: '#475c27' },
  pine:   { light: '#6f9c68', mid: '#4a7c4c', dark: '#335c39', line: '#2a4b31' },
};

function drawTrunk(ctx: CanvasRenderingContext2D, o: {
  cx: number; base: number; crownY: number; tw: number; lean: number; h: number; w: number; seed: number; kind: TreeKind;
}) {
  const { cx, base, crownY, tw, lean, h, w, seed, kind: k } = o;
  // A trunk is not a stick: it flares into the ground and narrows towards the crown.
  const flare = tw * 1.75;
  const mid = (crownY + base) / 2;
  const left: [number, number][] = [
    [cx - flare, base], [cx - tw * 1.12, base - h * 0.1], [cx - tw * 0.92 + lean * 0.4, mid], [cx - tw * 0.62 + lean, crownY + h * 0.03],
  ];
  const right: [number, number][] = [
    [cx + tw * 0.64 + lean, crownY + h * 0.03], [cx + tw * 0.95 + lean * 0.4, mid], [cx + tw * 1.15, base - h * 0.1], [cx + flare, base],
  ];
  if (k.trunk === 'gnarled') { left[2]![0] -= tw * 0.3; right[1]![0] += tw * 0.35; }
  pencil(ctx, [...left, ...right], {
    color: PENCIL, width: W.contour, seed, amp: 0.7, smooth: true,
    fill: C.trunk, fillAlpha: 0.78, shade: C.trunkDark, shadeAlpha: 0.32, gap: 3, angle: -1.44,
  });
  // roots reaching out of the flare
  if (k.roots) for (const sdir of [-1, 1] as const) {
    inkPath(ctx, [[cx + sdir * flare * 0.8, base - 2], [cx + sdir * (flare + w * 0.07), base], [cx + sdir * (flare + w * 0.13), base + 3]],
      { color: PENCIL, width: W.detail, seed: seed + sdir * 3, amp: 0.5, alpha: 0.8 });
  }
  // bark
  if (k.bark === 'ticks') {
    const nt = Math.max(9, Math.round((base - crownY) / 16));
    for (let i = 0; i < nt; i++) {
      const by = base - 4 - i * ((base - crownY) / (nt + 1)), side = i % 2 ? 0.4 : -0.35;
      inkPath(ctx, [[cx + tw * (side - 0.28), by], [cx + tw * side, by - 2], [cx + tw * (side + 0.26), by - 1]],
        { color: C.trunkDark, width: W.shade, seed: seed + i, amp: 0.3, alpha: 0.6 });
    }
  } else if (k.bark === 'grain') {
    const ng = Math.max(4, Math.round(tw / 4));
    for (let i = 0; i < ng; i++) {
      const gx = cx + (i - (ng - 1) / 2) * tw * (1.7 / ng);
      inkPath(ctx, [[gx, base - 3], [gx + lean * 0.4, mid], [gx + lean * 0.75, crownY + 4]],
        { color: C.trunkDark, width: W.shade, seed: seed + i * 5, amp: 0.4, alpha: 0.5 });
    }
  } else if (k.bark === 'plates') {
    const np = Math.max(10, Math.round((base - crownY) / 15));
    for (let i = 0; i < np; i++) {
      const by = base - 5 - i * ((base - crownY) / (np + 1)), side = (i % 3) - 1;
      inkPath(ctx, [[cx + side * tw * 0.45 - 2.4, by], [cx + side * tw * 0.45 + 2.4, by - 1], [cx + side * tw * 0.45 + 2, by + 3], [cx + side * tw * 0.45 - 2.4, by + 3.4]],
        { color: C.trunkDark, width: W.shade, seed: seed + i * 3, amp: 0.3, alpha: 0.4, close: true });
    }
  }
  // branches, drawn before the foliage so the crown sits on top of them
  const n = k.canopy === 'sparse' ? 4 : k.canopy === 'conic' ? 0 : 3;
  for (let i = 0; i < n; i++) {
    const sdir = i % 2 ? 1 : -1;
    const by = crownY + h * 0.03 + (i >> 1) * h * 0.07;
    // a branch tapers away from the trunk; on a big tree this used to come out as a ten pixel
    // black bar sticking out of the crown
    const bw = Math.min(5.5, Math.max(W.detail, tw * (0.34 - i * 0.06)));
    inkPath(ctx, [[cx + lean, by], [cx + lean + sdir * w * 0.13, by - h * 0.04], [cx + lean + sdir * w * 0.24, by - h * 0.1]],
      { color: C.trunkDark, width: bw, seed: seed + 10 + i, amp: 0.5, alpha: 0.9 });
    inkPath(ctx, [[cx + lean + sdir * w * 0.2, by - h * 0.085], [cx + lean + sdir * w * 0.3, by - h * 0.13]],
      { color: C.trunkDark, width: Math.max(W.shade, bw * 0.5), seed: seed + 30 + i, amp: 0.5, alpha: 0.8 });
  }
}

/**
 * The crown is built out of overlapping clumps of leaves, back to front, each one lighter than the
 * one behind it. That order is the whole trick: it is what makes a mass of green read as a tree
 * with depth rather than as a flat blob with a texture over it.
 */
function drawCrown(ctx: CanvasRenderingContext2D, o: {
  cx: number; crownY: number; w: number; h: number; seed: number; k: TreeKind; shade: Greens;
}) {
  const { cx, crownY, w, h, seed, k, shade } = o;
  const spread = { round: [0.52, 0.38], tall: [0.4, 0.48], wide: [0.62, 0.34], sparse: [0.5, 0.34], conic: [0.5, 0.42] }[k.canopy]!;
  const rx = w * spread[0]! * 0.88, ry = h * spread[1]! * 0.92;
  const cy = crownY - ry * 0.62;

  // Detail has to grow with the drawing. A crown twice as wide covers four times the page, so a
  // fixed number of clumps and leaf marks leaves it empty in the middle.
  const grow = Math.min(2.6, Math.max(1, rx / 58));
  type Clump = { x: number; y: number; r: number; ry: number; lit: number };
  const clumps: Clump[] = [];
  const n = Math.round((k.masses + 2) * grow);
  for (let m = 0; m < n; m++) {
    const a = (m / n) * Math.PI * 2 + rnd(seed, m) * 0.5;
    const d = m === 0 ? 0 : 0.46 + rnd(seed + 7, m) * 0.18;
    const px = cx + Math.cos(a) * rx * d, py = cy + Math.sin(a) * ry * d;
    // the clumps hanging at the front are smaller, so the near side does not become one flat mass
    const near = Math.max(0, Math.sin(a)) * d;
    const r = rx * (m === 0 ? 0.62 : (0.4 + rnd(seed + 3, m) * 0.16) * (1 - near * 0.3));
    // lit from the upper left: the further up and left a clump sits, the lighter it is drawn
    // the light comes over the top left shoulder, so the shadow falls to the lower right only
    const lit = 0.6 - (Math.cos(a) * d * 0.36) - (Math.sin(a) * d * 0.4);
    clumps.push({ x: px, y: py, r, ry: r * (ry / rx) * 1.35, lit: m === 0 ? 0.5 : lit });
  }
  clumps.sort((a, b) => (a.y - a.ry) - (b.y - b.ry));   // the back of the crown first

  for (let i = 0; i < clumps.length; i++) {
    const c = clumps[i]!;
    const lobes = Math.round((7 + (i % 3)) * Math.min(1.8, Math.max(1, c.r / 34)));
    const pts: [number, number][] = [];
    const steps = lobes * 3;
    for (let q = 0; q < steps; q++) {
      const t = (q / steps) * Math.PI * 2;
      const scallop = 1 + Math.cos(t * lobes) * 0.16 + (rnd(seed + i * 11, q) - 0.5) * 0.07;
      pts.push([c.x + Math.cos(t) * c.r * scallop, c.y + Math.sin(t) * c.ry * scallop]);
    }
    const fill = c.lit > 0.58 ? shade.light : c.lit > 0.33 ? shade.mid : shade.dark;
    const front = i >= clumps.length - 2;
    pencil(ctx, pts, {
      color: shade.line, width: front ? W.edge : W.shade, seed: seed + i * 5, amp: 0.45, smooth: true,
      fill, fillAlpha: 0.97, alpha: front ? 0.95 : 0.45,
      shade: c.lit < 0.33 ? shade.dark : undefined, shadeAlpha: 0.14, gap: 4.4, angle: -0.6,
    });
    // a smaller tuft sitting on top of the clump, to break the flat fill
    if (c.r > 26) {
      const tr = c.r * 0.46;
      const tx = c.x - c.r * 0.22, ty = c.y - c.ry * 0.3;
      const tl = 11, tp: [number, number][] = [];
      for (let q = 0; q < tl * 3; q++) {
        const t2 = (q / (tl * 3)) * Math.PI * 2;
        tp.push([tx + Math.cos(t2) * tr * (1 + Math.cos(t2 * tl) * 0.12), ty + Math.sin(t2) * tr * (c.ry / c.r) * (1 + Math.cos(t2 * tl) * 0.12)]);
      }
      pencil(ctx, tp, { color: shade.line, width: W.shade, seed: seed + i * 23, amp: 0.5, smooth: true,
        fill: c.lit > 0.5 ? shade.light : shade.mid, fillAlpha: 0.45, alpha: 0.3 });
    }
    // leaf ticks inside each clump, as many as the clump is big
    const marks = Math.round((k.canopy === 'sparse' ? 3 : 5) * Math.min(4, Math.max(1, (c.r * c.ry) / 700)));
    for (let q = 0; q < marks; q++) {
      const a2 = rnd(seed + i * 17, q) * Math.PI * 2, d2 = Math.sqrt(rnd(seed + i * 19, q)) * 0.7;
      const lx = c.x + Math.cos(a2) * c.r * d2, ly = c.y + Math.sin(a2) * c.ry * d2;
      if (k.foliage === 'needle') {
        inkLine(ctx, lx - 2, ly + 2, lx + 2, ly - 3, { color: shade.line, width: W.shade, amp: 0.3, alpha: 0.45, seed: seed + q });
      } else {
        inkPath(ctx, [[lx - 2.6, ly + 1.4], [lx, ly - 2.6], [lx + 2.6, ly + 1.4]],
          { color: shade.line, width: W.shade, seed: seed + i * 7 + q, amp: 0.3, alpha: 0.42 });
      }
    }
  }
  // leaves catching the edge of the silhouette, spaced by the size of the crown
  const rim = Math.round((k.canopy === 'sparse' ? 10 : 16) * grow);
  for (let i = 0; i < rim; i++) {
    const a = (i / rim) * Math.PI * 2 + rnd(seed + 31, i) * 0.3;
    const lx = cx + Math.cos(a) * rx * (0.96 + rnd(seed + 33, i) * 0.12);
    const ly = cy + Math.sin(a) * ry * (0.96 + rnd(seed + 35, i) * 0.12);
    const la = a + 0.45, len = 4.5 + rnd(seed + 37, i) * 3;
    const tx = lx + Math.cos(la) * len, ty = ly + Math.sin(la) * len;
    const nx = -Math.sin(la) * len * 0.3, ny = Math.cos(la) * len * 0.3;
    inkPath(ctx, [[lx, ly], [lx + (tx - lx) * 0.45 + nx, ly + (ty - ly) * 0.45 + ny], [tx, ty], [lx + (tx - lx) * 0.45 - nx, ly + (ty - ly) * 0.45 - ny]],
      { color: shade.line, width: W.shade, seed: seed + i, amp: 0.25, close: true, alpha: 0.75, fill: shade.mid });
  }
}

/** a conifer: skirts of needles stepping down and out, darkest at the bottom */
function drawConifer(ctx: CanvasRenderingContext2D, o: {
  cx: number; crownY: number; w: number; h: number; seed: number; shade: Greens;
}) {
  const { cx, crownY, w, h, seed, shade } = o;
  const layers = Math.max(6, Math.round(h / 44));
  for (let i = 0; i < layers; i++) {
    const t = i / layers;
    const ly = crownY + h * 0.08 - t * h * 0.66;
    const lw = w * (0.5 - t * 0.36);
    const droop = h * 0.055;
    const teeth = Math.max(5, Math.round(lw / 6));
    const pts: [number, number][] = [[cx - lw, ly]];
    for (let q = 0; q <= teeth; q++) {
      const f = q / teeth;
      pts.push([cx - lw + f * lw * 2, ly - (q % 2 ? 4 : 1.6) - Math.sin(f * Math.PI) * droop * 0.5]);
    }
    pts.push([cx + lw, ly], [cx + lw * 0.55, ly - h * 0.07], [cx, ly - h * 0.15], [cx - lw * 0.55, ly - h * 0.07]);
    pencil(ctx, pts, {
      color: shade.line, width: i === 0 ? W.edge : W.detail, seed: seed + i * 11, amp: 0.45,
      fill: i < 2 ? shade.dark : i < 4 ? shade.mid : shade.light, fillAlpha: 0.97,
      shade: i < 3 ? shade.dark : undefined, shadeAlpha: 0.14, gap: 4, angle: -0.5,
    });
    // needles hanging off the edge of each skirt
    const nn = Math.max(9, Math.round(lw / 7));
    for (let q = 0; q < nn; q++) {
      const f = rnd(seed + i, q), nx = cx - lw + f * lw * 2;
      const dip = ly - 1 + Math.sin(f * Math.PI) * 1.5;
      inkLine(ctx, nx, dip, nx + (q % 2 ? 3.5 : -3.5), dip + 4.5, { color: shade.line, width: W.shade, amp: 0.3, alpha: 0.55, seed: seed + i * 7 + q });
    }
  }
}

// ---------- shadow ----------
function scribbleShadow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, seed: number) {
  for (let i = 0; i < 3; i++) inkLine(ctx, x - r, y + i * 2 - 2, x + r, y + i * 2 - 2, { color: PENCIL, width: 1.2, amp: 1.4, alpha: 0.22, seed: seed + i });
}

// ---------- characters ----------
// The player figure lives in hero.ts: it has to know about worn equipment.

export function drawEnemy(ctx: CanvasRenderingContext2D, e: EntityState, t: number, swing: number, showName = true) {
  const x = e.x, y = e.y, seed = hashStr(e.id);
  const moving = e.v > 5;
  // six frames of the idle or walk cycle, plus a lunge frame while it strikes
  const phase = Math.floor((((t * (moving ? 9 : 2) + seed) % (Math.PI * 2)) / (Math.PI * 2)) * 6);
  const sw = swing > 0.05 ? 1 : 0;
  const baked = bake(`foe|${e.def}|${moving ? 'w' : 'i'}|${phase}|${sw}`, { x: -44, y: -54, w: 88, h: 60 }, spriteScale(),
    (c: CanvasRenderingContext2D) => drawEnemyFrame(c, e.def, phase / 6, moving, sw ? 1 : 0));
  scribbleShadow(ctx, x, y + 2, 12, seed);
  if (baked) blit(ctx, baked, x, y);
  if (showName) {
    penText(ctx, e.name ?? '', x, y - 44, { size: 12, align: 'center', color: PENCIL, seed: seed + 5, alpha: 0.7 });
    if (e.hp !== undefined && e.hpMax && e.hp < e.hpMax) pipBar(ctx, x - 15, y - 38, 30, e.hp / e.hpMax, seed, RED);
  }
}

/** one frame of a creature, drawn around its own feet */
function drawEnemyFrame(ctx: CanvasRenderingContext2D, def: string, k: number, moving: boolean, swing: number) {
  const seed = 11;
  const t = k * Math.PI * 2;
  const e = { def } as EntityState;
  const hop = Math.abs(Math.sin(t)) * (moving ? 3 : 1);
  const lunge = swing > 0.05 ? swing * 4 : 0;
  ctx.save();
  ctx.translate(0, -hop);
  switch (e.def) {
    case 'scribble_rat': {
      const wag = Math.sin(t * 7 + seed) * 6;
      // tail first, behind the body: a long curl of pen
      inkPath(ctx, [[-13, -11], [-22, -6], [-30, -12 + wag * 0.4], [-27, -20 + wag]], { color: '#6b5a4a', width: 2.2, seed, amp: 1.1 });
      // body: two overlapping scribbled ovals, hatched underneath
      inkEllipse(ctx, -1, -12, 15, 10.5, { color: '#4a3d31', width: 2.6, seed: seed + 1, amp: 1.5, double: true, fill: '#b9a894' });
      hatch(ctx, -10, -12, 18, 10, { color: '#6b5a4a', gap: 3.4, angle: -1.15, alpha: 0.45, seed: seed + 2 });
      // the lit back and the paler belly
      ctx.save(); ctx.globalAlpha = 0.45; ctx.fillStyle = '#ded3c3';
      ctx.beginPath(); ctx.ellipse(-3, -15, 10, 5, -0.2, 0, 7); ctx.fill(); ctx.restore();
      inkEllipse(ctx, 4, -15, 9, 7, { color: '#6b5a4a', width: 1.4, seed: seed + 3, amp: 1.2, alpha: 0.7 });
      // legs
      for (const leg of [[-7, -3], [2, -2], [8, -3]] as const) { const [lx, ly] = leg; inkLine(ctx, lx, ly - 2, lx + (moving ? Math.sin(t * 9 + lx) * 2 : 0), ly + 3, { color: '#4a3d31', width: 2, amp: 0.7, seed: seed + lx + 9 }); }
      // head with a pointed snout, ears and whiskers
      const hx = 12 + lunge;
      inkPath(ctx, [[hx - 5, -20], [hx + 7, -17], [hx + 11, -12], [hx + 6, -8], [hx - 4, -9]], { color: '#4a3d31', width: 2.4, seed: seed + 4, amp: 1, close: true, fill: '#c8b9a5' });
      inkPath(ctx, [[hx - 2, -20], [hx - 5, -27], [hx + 3, -23]], { color: '#4a3d31', width: 1.8, seed: seed + 5, amp: 0.9, close: true, fill: '#d9a9a9' });
      inkPath(ctx, [[hx + 4, -19], [hx + 4, -26], [hx + 9, -20]], { color: '#4a3d31', width: 1.8, seed: seed + 6, amp: 0.9, close: true, fill: '#d9a9a9' });
      ctx.fillStyle = INK; ctx.beginPath(); ctx.arc(hx + 5, -16, 1.5, 0, 7); ctx.fill();
      ctx.fillStyle = RED; ctx.beginPath(); ctx.arc(hx + 10.5, -12, 1.6, 0, 7); ctx.fill();
      for (const wy of [-13.5, -11]) { inkLine(ctx, hx + 9, wy, hx + 18, wy - 2, { color: PENCIL, width: 0.8, amp: 0.7, alpha: 0.6, seed: seed + wy + 30 }); }
      break;
    }
    case 'ink_slime': {
      const r = 22 + Math.sin(t * 3 + seed) * 1.8;
      const squash = 1 + Math.sin(t * 3 + seed) * 0.06;
      // a blob is round: built from a dome that sits flat on the ground, smoothed through its points
      const blob: [number, number][] = [];
      const bn = 13;
      for (let i = 0; i < bn; i++) {
        const a2 = Math.PI + (i / (bn - 1)) * Math.PI;
        blob.push([Math.cos(a2) * r * (0.96 + rnd(seed, i) * 0.1), -1 + Math.sin(a2) * r * 1.12 * squash * (0.94 + rnd(seed + 5, i) * 0.12)]);
      }
      blob.push([r, -1], [r * 0.5, 1], [-r * 0.5, 1]);
      pencil(ctx, blob, { color: INK, width: 3, seed, amp: 1.1, smooth: true, double: true, fill: '#5d7fc0', fillAlpha: 0.72 });
      hatch(ctx, 1, -r * 1.1, r, r, { color: INK, gap: 3.6, angle: -1.2, alpha: 0.3, seed: seed + 1 });
      // the gloss on the top left, the way a wet blob catches the light
      ctx.save(); ctx.globalAlpha = 0.5; ctx.fillStyle = '#ffffff';
      ctx.beginPath(); ctx.ellipse(-r * 0.4, -r * 0.92, r * 0.3, r * 0.2, -0.5, 0, 7); ctx.fill(); ctx.restore();
      inkPath(ctx, [[-8, -r * 1.05], [-3, -r * 1.15], [-1, -r * 0.95]], { color: '#ffffff', width: 1.6, seed: seed + 2, amp: 0.6, alpha: 0.8 });
      const drip = (Math.sin(t * 0.9 + seed) + 1) * 4;
      inkPath(ctx, [[r * 0.75, -6], [r * 0.85, -2 + drip], [r * 0.7, -4 + drip]], { color: INK, width: 1.4, seed: seed + 3, amp: 0.7, close: true, fill: 'rgba(32,54,110,0.4)' });
      ctx.fillStyle = INK;
      ctx.beginPath(); ctx.arc(-6, -19, 2.5, 0, 7); ctx.fill();
      ctx.beginPath(); ctx.arc(6, -19, 2.5, 0, 7); ctx.fill();
      ctx.fillStyle = '#f6f2e4';
      ctx.beginPath(); ctx.arc(-6.8, -19.9, 0.9, 0, 7); ctx.fill();
      ctx.beginPath(); ctx.arc(5.2, -19.9, 0.9, 0, 7); ctx.fill();
      inkPath(ctx, [[-5, -11], [0, -8.5], [5, -11]], { color: INK, width: 1.4, seed: seed + 4, amp: 0.6 });
      if (swing > 0.05) inkBlot(ctx, 12, -4, 2.6, INK, seed + 5, 0.5);
      break;
    }
    case 'paper_bat': {
      const flap = Math.sin(t * 11 + seed);
      const lift = -30 - flap * 3;
      // torn paper wings: jagged edges, fold lines
      for (const s of [-1, 1] as const) {
        const w = 24 + flap * s * 2;
        inkPath(ctx, [[s * 5, lift + 2], [s * (w * 0.5), lift - 9 - flap * 5 * s], [s * w, lift - 3 - flap * 4 * s], [s * (w * 0.8), lift + 4], [s * (w * 0.45), lift + 1], [s * (w * 0.55), lift + 7], [s * 6, lift + 6]],
          { color: '#4a4a44', width: 2.4, seed: seed + s * 3, amp: 1.1, close: true, fill: s < 0 ? '#f4efe2' : '#ddd6c6' });
        inkLine(ctx, s * 7, lift + 3, s * (w * 0.75), lift - 2, { color: PENCIL, width: 0.9, amp: 0.8, alpha: 0.55, seed: seed + s * 5 });
        inkLine(ctx, s * 7, lift + 4, s * (w * 0.55), lift + 5, { color: PENCIL, width: 0.9, amp: 0.8, alpha: 0.45, seed: seed + s * 7 });
      }
      inkEllipse(ctx, 0, lift, 6.5, 8, { color: '#4a4a44', width: 2.6, seed: seed + 9, amp: 0.9, double: true, fill: '#e8e1d2' });
      inkPath(ctx, [[-4, lift - 6], [-2, lift - 11], [0, lift - 6]], { color: PENCIL, width: 1.3, seed: seed + 10, amp: 0.8, close: true });
      inkPath(ctx, [[1, lift - 6], [3, lift - 11], [5, lift - 6]], { color: PENCIL, width: 1.3, seed: seed + 11, amp: 0.8, close: true });
      ctx.fillStyle = RED;
      ctx.beginPath(); ctx.arc(-2.2, lift - 1, 1.4, 0, 7); ctx.fill();
      ctx.beginPath(); ctx.arc(2.6, lift - 1, 1.4, 0, 7); ctx.fill();
      inkLine(ctx, -2, lift + 4, 2, lift + 4, { color: PENCIL, width: 1, amp: 0.5, seed: seed + 12 });
      break;
    }
    default:
      inkEllipse(ctx, 0, -14, 14, 12, { color: PENCIL, width: 2, seed, amp: 2 });
  }
  ctx.restore();
}

/** what colour a find is written in: the better it is, the louder */
const RARITY: Record<string, string> = {
  common: '#6a6a64', uncommon: '#2a7340', rare: '#2f5fb0', relic: '#7b46b0', mythic: '#c99024',
};

export function drawLoot(ctx: CanvasRenderingContext2D, e: EntityState, t: number) {
  const x = e.x, seed = hashStr(e.id);
  const bob = Math.sin(t * 2.4 + seed) * 2.5;
  const y = e.y + bob;
  const def = ITEMS[e.def ?? ''];
  const col = RARITY[def?.rarity ?? 'common'] ?? MARGIN;
  // the shadow stays on the ground while the thing bobs above it
  ctx.save(); ctx.globalAlpha = 0.16;
  hatch(ctx, x - 11, e.y - 2, 22, 7, { color: PENCIL, gap: 3, angle: -0.5, alpha: 0.6, seed: seed + 3 });
  ctx.restore();
  // a scrap of paper the find is lying on, torn along the bottom
  const pts: [number, number][] = [[x - 13, y - 26], [x + 13, y - 26], [x + 13, y - 6]];
  for (let i = 4; i >= 0; i--) pts.push([x - 13 + (i / 4) * 26, y - 6 + (i % 2 ? 3 : 0)]);
  inkPath(ctx, pts, { color: col, width: W.edge, seed, amp: 0.6, close: true, fill: 'rgba(253,251,244,0.95)' });
  if (def) itemIcon(ctx, def.id, x, y - 16, 19, seed + 1);
  else inkBlot(ctx, x, y - 16, 4, INK, seed, 0.5);
  // the name above it, in the colour of its rarity
  penText(ctx, def?.name ?? 'something', x, y - 31, { size: 12, align: 'center', color: col, seed: seed + 2 });
  // a relic is worth a few sparks
  if (def && (def.rarity === 'relic' || def.rarity === 'mythic')) {
    for (let i = 0; i < 4; i++) {
      const a = t * 1.6 + i * 1.57 + seed;
      const sx = x + Math.cos(a) * 15, sy = y - 16 + Math.sin(a) * 11;
      inkLine(ctx, sx - 2, sy, sx + 2, sy, { color: col, width: W.shade, amp: 0.2, alpha: 0.7, seed: seed + i });
      inkLine(ctx, sx, sy - 2, sx, sy + 2, { color: col, width: W.shade, amp: 0.2, alpha: 0.7, seed: seed + i + 9 });
    }
  }
}

/** a little row of pen ticks instead of a bar */
export function pipBar(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, k: number, seed = 1, color = GREEN) {
  const n = 8, gap = w / n;
  for (let i = 0; i < n; i++) {
    const on = i / n < k;
    inkLine(ctx, x + i * gap + 1, y + 3, x + i * gap + gap - 2, y, { color: on ? color : PENCIL, width: on ? 2.4 : 1, amp: 0.8, seed: seed + i, alpha: on ? 0.95 : 0.3 });
  }
}

export function hashStr(s: string) { let h = 2166136261 >>> 0; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; } return h % 100000; }

// ---------- ground clutter and the doodle layer ----------
// The page is not empty between the landmarks. Each cell of the world gets a handful of small
// drawings — pebbles, twigs, tufts, mushrooms, half-erased marks — and, more rarely, something
// somebody scribbled over the top: an arrow, a crossed-out attempt, a note in the margin.
const SUB = 150;                 // the grid the little drawings are generated on
const CELL = SUB * 3;            // the grid the page is baked on; one canvas covers nine patches

// The roads are drawn as worn earth rather than as lines: the page shows where people walk.
const PATHS: [number, number][][] = [
  [[600, 1760], [940, 1640], [1220, 1580], [1400, 1520], [1640, 1440], [1920, 1380], [2120, 1372], [2320, 1400], [2540, 1580], [2700, 1800], [2840, 2020], [3040, 2180]],
  [[1400, 1540], [1520, 1760], [1600, 2020], [1720, 2260], [1800, 2500], [1860, 2720]],
  [[760, 1320], [940, 1380], [1120, 1440], [1320, 1490]],
];

function drawPaths(ctx: CanvasRenderingContext2D, bx: number, by: number, bw: number, bh: number) {
  for (let pi = 0; pi < PATHS.length; pi++) {
    const line = PATHS[pi]!;
    for (let i = 0; i < line.length - 1; i++) {
      const [ax, ay] = line[i]!, [cx2, cy2] = line[i + 1]!;
      // skip the whole segment when it cannot touch this tile
      const pad = 44;
      if (Math.max(ax, cx2) < bx - pad || Math.min(ax, cx2) > bx + bw + pad) continue;
      if (Math.max(ay, cy2) < by - pad || Math.min(ay, cy2) > by + bh + pad) continue;
      const len = Math.hypot(cx2 - ax, cy2 - ay), steps = Math.max(2, Math.round(len / 15));
      for (let k = 0; k <= steps; k++) {
        const t = k / steps, px = ax + (cx2 - ax) * t, py = ay + (cy2 - ay) * t;
        if (px < bx - pad || px > bx + bw + pad || py < by - pad || py > by + bh + pad) continue;
        const seed = pi * 977 + i * 131 + k;
        scribble(ctx, px, py, 34, 26, { color: C.earth, alpha: 0.54, n: 14, len: 15, angle: Math.atan2(cy2 - ay, cx2 - ax), seed, width: 1.2 });
        if (k % 3 === 0) scribble(ctx, px, py, 24, 18, { color: C.earthDark, alpha: 0.24, n: 4, len: 11, angle: Math.atan2(cy2 - ay, cx2 - ax) + 0.4, seed: seed + 7 });
        if (rnd(seed, 3) > 0.84) {
          const sx = px + (rnd(seed, 4) - 0.5) * 44, sy = py + (rnd(seed, 5) - 0.5) * 34;
          inkPath(ctx, [[sx, sy], [sx + 3, sy - 2.4], [sx + 6, sy - 0.8], [sx + 5, sy + 1.8]],
            { color: PENCIL_SOFT, width: W.shade, seed: seed + 9, amp: 0.3, close: true, alpha: 0.5 });
        }
      }
    }
  }
}

let doodlesOn = true;
export const setDoodles = (v: boolean) => { doodlesOn = v; };

export function drawClutterCell(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  // colour goes down first and details on top of it, the way a page is actually coloured in
  for (let j = 0; j < 3; j++) for (let i = 0; i < 3; i++) groundWash(ctx, cx * 3 + i, cy * 3 + j);
  drawPaths(ctx, cx * CELL, cy * CELL, CELL, CELL);
  for (let j = 0; j < 3; j++) for (let i = 0; i < 3; i++) clutterPatch(ctx, cx * 3 + i, cy * 3 + j);
}

/** the ground itself: soft green where things grow, bare earth where they do not */
function groundWash(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  const seed = ((cx * 22697) ^ (cy * 34613) ^ 0x2b1d) >>> 0;
  const n = 4 + Math.floor(rnd(seed, 0) * 3);
  for (let i = 0; i < n; i++) {
    const gx = cx * SUB + rnd(seed + 1, i) * SUB, gy = cy * SUB + rnd(seed + 2, i) * SUB;
    const earth = rnd(seed + 3, i) > 0.82;
    const rx = SUB * (0.16 + rnd(seed + 4, i) * 0.2), ry = SUB * (0.13 + rnd(seed + 5, i) * 0.16);
    scribble(ctx, gx, gy, rx, ry, {
      color: earth ? C.earth : C.grass, alpha: earth ? 0.52 : 0.56,
      n: Math.round(rx * ry / 34), len: earth ? 13 : 10,
      angle: earth ? -0.2 : -0.9 + rnd(seed + 6, i) * 0.5, seed: seed + i * 17, width: earth ? 1.1 : 0.85,
    });
    if (!earth) scribble(ctx, gx, gy, rx * 0.66, ry * 0.66, { color: C.grassDark, alpha: 0.32, n: Math.round(rx * ry / 150), len: 7, angle: -1.15, seed: seed + i * 29 });
  }
}

/** rarer, larger marks over the page: arrows, crossings out, notes, blots */
export function drawDoodleCell(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  if (!doodlesOn) return;
  for (let j = 0; j < 3; j++) for (let i = 0; i < 3; i++) doodlePatch(ctx, cx * 3 + i, cy * 3 + j);
}

function clutterPatch(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  const seed = ((cx * 73856093) ^ (cy * 19349663)) >>> 0;
  const n = 7 + Math.floor(rnd(seed, 0) * 5);
  for (let i = 0; i < n; i++) {
    const x = cx * SUB + rnd(seed + 1, i) * SUB;
    const y = cy * SUB + rnd(seed + 2, i) * SUB;
    const kind = Math.floor(rnd(seed + 3, i) * 7);
    switch (kind) {
      case 0: {   // pebbles
        for (let k = 0; k < 2 + Math.floor(rnd(seed + 4, i + k) * 2); k++) {
          const px = x + k * 5, py = y + (k % 2) * 3;
          inkPath(ctx, [[px, py], [px + 3, py - 2.5], [px + 6, py - 1], [px + 5, py + 2]], { color: PENCIL_SOFT, width: W.shade, seed: seed + i * 7 + k, amp: 0.3, close: true, alpha: 0.6 });
        }
        break;
      }
      case 1: {   // a twig
        inkPath(ctx, [[x, y], [x + 9, y - 3], [x + 15, y - 1]], { color: PENCIL_SOFT, width: W.shade, seed: seed + i, amp: 0.4, alpha: 0.55 });
        inkLine(ctx, x + 9, y - 3, x + 12, y - 7, { color: PENCIL_SOFT, width: 0.5, amp: 0.3, alpha: 0.5, seed: seed + i + 9 });
        break;
      }
      case 2: {   // a tuft of grass
        for (let k = 0; k < 4; k++) inkPath(ctx, [[x + k * 2.4, y], [x + k * 2.4 + (k - 2), y - 6 - rnd(seed + 5, i + k) * 4]], { color: GREEN, width: W.shade, seed: seed + i * 5 + k, amp: 0.3, alpha: 0.6 });
        break;
      }
      case 3: {   // mushrooms
        for (let k = 0; k < 2; k++) {
          const mx = x + k * 7, my = y - k * 2;
          inkLine(ctx, mx, my, mx, my - 4, { color: PENCIL_SOFT, width: W.shade, amp: 0.2, alpha: 0.6, seed: seed + i + k });
          inkPath(ctx, [[mx - 3.2, my - 4], [mx, my - 6.5], [mx + 3.2, my - 4]], { color: RED, width: W.shade, seed: seed + i * 3 + k, amp: 0.3, close: true, alpha: 0.5 });
        }
        break;
      }
      case 4: {   // a half-erased pencil mark
        hatch(ctx, x, y - 6, 16, 10, { gap: 4, angle: -0.5, alpha: 0.08, seed: seed + i });
        break;
      }
      case 5: {   // a few fallen leaves
        for (let k = 0; k < 3; k++) {
          const lx = x + k * 6, ly = y + (k % 2) * 4;
          inkPath(ctx, [[lx, ly], [lx + 3, ly - 2.5], [lx + 6, ly]], { color: GREEN, width: W.shade, seed: seed + i * 11 + k, amp: 0.3, alpha: 0.45 });
        }
        break;
      }
      default: {  // a small ink dot and its spatter
        inkBlot(ctx, x, y, 1.6 + rnd(seed + 6, i) * 1.4, INK, seed + i, 0.35);
      }
    }
  }
}

function doodlePatch(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  const seed = ((cx * 40503) ^ (cy * 12289) ^ 0x5f3a) >>> 0;
  if (rnd(seed, 0) > 0.42) return;
  const x = cx * SUB + 20 + rnd(seed, 1) * (SUB - 40);
  const y = cy * SUB + 20 + rnd(seed, 2) * (SUB - 40);
  const kind = Math.floor(rnd(seed, 3) * 5);
  switch (kind) {
    case 0: {   // an arrow pointing at nothing in particular
      const a = rnd(seed, 4) * 6.28, len = 30 + rnd(seed, 5) * 26;
      const ex = x + Math.cos(a) * len, ey = y + Math.sin(a) * len;
      inkPath(ctx, [[x, y], [x + Math.cos(a) * len * 0.5 - Math.sin(a) * 8, y + Math.sin(a) * len * 0.5 + Math.cos(a) * 8], [ex, ey]],
        { color: RED, width: W.detail, seed, amp: 0.8, alpha: 0.4 });
      inkLine(ctx, ex, ey, ex - Math.cos(a - 0.5) * 9, ey - Math.sin(a - 0.5) * 9, { color: RED, width: W.detail, amp: 0.5, alpha: 0.4, seed: seed + 1 });
      inkLine(ctx, ex, ey, ex - Math.cos(a + 0.5) * 9, ey - Math.sin(a + 0.5) * 9, { color: RED, width: W.detail, amp: 0.5, alpha: 0.4, seed: seed + 2 });
      break;
    }
    case 1: {   // a drawing that did not work out, crossed through
      const w = 26 + rnd(seed, 6) * 22, h = 20 + rnd(seed, 7) * 18;
      inkPath(ctx, [[x, y + h], [x + w * 0.2, y + h * 0.3], [x + w * 0.55, y], [x + w, y + h * 0.5], [x + w * 0.8, y + h]],
        { color: PENCIL_SOFT, width: W.shade, seed, amp: 0.7, close: true, alpha: 0.28 });
      inkLine(ctx, x - 3, y - 3, x + w + 3, y + h + 3, { color: RED, width: W.detail, amp: 1, alpha: 0.35, seed: seed + 3 });
      inkLine(ctx, x + w + 3, y - 3, x - 3, y + h + 3, { color: RED, width: W.detail, amp: 1, alpha: 0.35, seed: seed + 4 });
      break;
    }
    case 2: {   // an underlined note
      const words = ['look here', 'careful', 'he was here', 'do not', 'ask Margit', 'page 2?', 'this way', 'mine'];
      const word = words[Math.floor(rnd(seed, 8) * words.length)]!;
      penText(ctx, word, x, y, { size: 15, color: MARGIN, alpha: 0.42, seed, tilt: (rnd(seed, 9) - 0.5) * 0.2 });
      inkLine(ctx, x - 2, y + 3, x + 12 + word.length * 5, y + 3, { color: MARGIN, width: W.shade, amp: 0.6, alpha: 0.35, seed: seed + 5 });
      break;
    }
    case 3: {   // a smudge where a hand dragged through wet ink
      hatch(ctx, x, y, 40 + rnd(seed, 10) * 30, 12, { color: INK_SOFT, gap: 3, angle: -0.15, alpha: 0.07, seed });
      break;
    }
    default: {  // an ink blot with spatter
      inkBlot(ctx, x, y, 3 + rnd(seed, 11) * 3.5, INK, seed, 0.3);
    }
  }
}

export const clutterCell = CELL;
